/* Il warmup minimo che una configurazione richiede.
 *
 * Perché sta nel frontend. Il warmup consuma l'inizio del periodo per formare
 * gli indicatori, e se è insufficiente il backtest NON fallisce: produce un
 * risultato completo, plausibile e falso. È misurato: la stessa configurazione
 * con warmup 2.400 invece di 2.700 dava 12,56% invece di 10,57% — sembrava
 * migliore. Un errore che non dà errore va intercettato prima di premere.
 *
 * Il calcolo è la STESSA euristica del backend (`src/modules/backtester/
 * warmup-validation.ts`), riportata qui carattere per carattere sui nomi dei
 * parametri: si prendono i parametri numerici il cui nome indica una finestra
 * (`*Period`, `*SMA`, `*MA`, `stDev*`, `*length`, `*window`, `*lookback`),
 * escludendo soglie, tipi e moltiplicatori, e si tiene il più lungo.
 * `ROC_period` e `RSI_period` si moltiplicano per `virtualCS`, perché quegli
 * indicatori sono aggiornati una volta ogni `virtualCS` candele.
 *
 * Ricalcolarla qui invece di chiederla al server non è una duplicazione a
 * cuor leggero: è l'unico modo di suggerire il valore MENTRE si compila, e la
 * copia è dichiarata perché resti riconoscibile se il backend cambia. La
 * richiesta di esporla come endpoint è annotata in RICHIESTE-API.md.
 *
 * È un'euristica sui nomi: può SOTTOSTIMARE il fabbisogno di una strategia che
 * usi convenzioni diverse. Nel dubbio si propone di più, mai di meno.
 */

const CHIAVE_PERIODO = /(period|lookback|length|window|stdev|sma$|ema$|wma$|smma$|ma$)/i;
const NON_E_PERIODO = /(treshold|threshold|type|mode|multiplier|factor|gate|gain|gamma)/i;
const SCALATO_DA_VIRTUAL_CS = /^(roc|rsi)_?period$/i;

export interface RichiestaWarmup {
  /** Candele del timeframe del backtest richieste dai parametri. */
  minimo: number;
  /** I parametri che lo determinano, dal più lungo: serve a dire PERCHÉ. */
  cause: { chiave: string; candele: number }[];
}

function positivo(valore: unknown): number | null {
  if (valore === null || valore === undefined || valore === '') return null;
  const n = Number(valore);
  return Number.isFinite(n) && n > 0 ? n : null;
}

/**
 * Il warmup minimo per questi parametri.
 *
 * In una griglia ogni parametro è una lista: si prende il valore più grande di
 * ciascuna, perché il warmup deve bastare alla combinazione più esigente della
 * griglia, non alla prima.
 */
export function warmupRichiesto(parametri: Record<string, unknown>): RichiestaWarmup {
  const massimoDi = (v: unknown): number | null => {
    if (Array.isArray(v)) {
      const numeri = v.map(positivo).filter((n): n is number => n != null);
      return numeri.length ? Math.max(...numeri) : null;
    }
    return positivo(v);
  };

  const virtualCS = massimoDi(parametri.virtualCS) ?? 1;
  const cause: { chiave: string; candele: number }[] = [];

  for (const [chiave, grezzo] of Object.entries(parametri)) {
    if (NON_E_PERIODO.test(chiave)) continue;
    if (!CHIAVE_PERIODO.test(chiave)) continue;
    const valore = massimoDi(grezzo);
    if (valore == null) continue;
    cause.push({
      chiave,
      candele: SCALATO_DA_VIRTUAL_CS.test(chiave) ? valore * virtualCS : valore,
    });
  }

  cause.sort((a, b) => b.candele - a.candele);
  return { minimo: cause.length ? cause[0].candele : 0, cause };
}

/**
 * Quanto periodo misurato si mangia il warmup, in giorni.
 *
 * È la traduzione che serve a decidere: «2.700 candele» non dice niente,
 * «112 giorni tolti all'inizio del periodo» sì.
 */
export function giorniDiWarmup(warmup: number, candleSizeMinuti: number): number | null {
  if (!Number.isFinite(warmup) || !Number.isFinite(candleSizeMinuti) || candleSizeMinuti <= 0) {
    return null;
  }
  return (warmup * candleSizeMinuti) / (60 * 24);
}
